// Simplified Tool Data Extraction System
//
// This replaces the complex multi-hook system with 3 simple functions:
// 1. hasToolData() - Check if message has any tool data
// 2. extractToolData() - Extract all tool data from message
// 3. extractSpecificToolData() - Extract specific type of tool data

import { parseToolCalls, parseStorageFiles } from "./parsingUtils";

/**
 * MAIN FUNCTION: Check if message has any tool data
 * @param {Object} message - The message object
 * @returns {boolean} - True if message has any tool data
 */
export function hasToolData(message) {
  if (!message) return false;

  // Check for tool calls (canvas, image-gen, web-search)
  if (message.tool_calls || message.metadata?.tool_calls) {
    return true;
  }

  // Check for citations (Perplexity citations in metadata)
  if (
    message.metadata?.citations &&
    Array.isArray(message.metadata.citations) &&
    message.metadata.citations.length > 0
  ) {
    return true;
  }

  // Check for deep research
  if (
    message.metadata?.isDeepResearch ||
    message.metadata?.hasDeepResearch ||
    message.metadata?.isDeepResearchResult ||
    message.metadata?.isDeepResearchInProgress ||
    message.metadata?.deepResearch
  ) {
    return true;
  }

  // Check for file attachments
  if (
    message.attachments?.length > 0 ||
    message.metadata?.storage_files ||
    message.metadata?.has_files
  ) {
    return true;
  }

  return false;
}

/**
 * MAIN FUNCTION: Extract all tool data from message
 * @param {Object} message - The message object
 * @param {boolean} isUser - Whether this is a user message
 * @returns {Object} - Object containing all extracted tool data
 */
export function extractToolData(message, isUser = false) {
  if (!hasToolData(message)) {
    return {
      hasAnyToolData: false,
      hasToolCalls: false,
      hasDeepResearch: false,
      hasFileAttachments: false,
      webSearchData: null,
    };
  }

  // Extract tool calls
  const toolCallsData = extractSpecificToolData(message, "toolCalls");

  // Extract deep research
  const deepResearchData = extractSpecificToolData(
    message,
    "deepResearch",
    isUser
  );

  // Extract file attachments
  const fileAttachmentsData = extractSpecificToolData(
    message,
    "fileAttachments"
  );

  // Extract specific tool types from tool calls
  const canvasData = toolCallsData.hasToolCalls
    ? extractSpecificToolData(message, "canvas")
    : null;
  const imageData = toolCallsData.hasToolCalls
    ? extractSpecificToolData(message, "image")
    : null;
  // CRITICAL FIX: Always extract web search data, not just when there are tool calls
  // This allows Perplexity citations (from metadata) to be extracted even without tool calls
  const webSearchData = extractSpecificToolData(message, "webSearch");

  // Convert generated images to file attachments so they display inline like uploaded images
  let generatedImageAttachments = [];
  if (imageData) {
    try {
      // Helper to extract path from URL
      const extractPathFromUrl = (imageUrl) => {
        if (!imageUrl) return null;
        if (imageUrl.includes("/storage/v1/object/public/chat/")) {
          const urlMatch = imageUrl.match(
            /\/storage\/v1\/object\/public\/chat\/(.+)$/
          );
          if (urlMatch && urlMatch[1]) {
            return decodeURIComponent(urlMatch[1]);
          }
        }
        return imageUrl;
      };

      // Helper to create attachment from image object
      const createAttachment = (image, index, metadata) => {
        const filePath = image.storagePath || extractPathFromUrl(image.url);
        return {
          id: `generated-image-${message.id}-${index}`,
          path: filePath || image.url,
          name: image.name || `Generated Image ${index + 1}`,
          type: "image/png",
          isImage: true,
          url: image.url,
          isGenerated: true,
          isExternalUrl: image.url?.startsWith("http"),
          metadata,
        };
      };

      // Extract metadata from various possible locations
      const metadata = {
        ...(imageData.result?.metadata || {}),
        ...(imageData.metadata || {}),
        ...(imageData.result?.has_nsfw_concepts !== undefined
          ? { has_nsfw_concepts: imageData.result.has_nsfw_concepts }
          : {}),
        ...(imageData.has_nsfw_concepts !== undefined
          ? { has_nsfw_concepts: imageData.has_nsfw_concepts }
          : {}),
      };

      // Check for images array first (new multi-image format)
      const imagesArray =
        imageData.result?.images ||
        imageData.result?.result?.images ||
        imageData.images ||
        [];

      if (imagesArray.length > 0) {
        // Multi-image: create attachment for each image
        generatedImageAttachments = imagesArray.map((image, index) =>
          createAttachment(image, index, metadata)
        );
      } else {
        // Fallback: single image extraction (backward compatibility)
        let imageUrl = null;
        let storagePath = null;
        let imageName = "Generated Image";

        if (typeof imageData === "string") {
          imageUrl = imageData;
        } else if (imageData.result?.data?.url) {
          imageUrl = imageData.result.data.url;
          storagePath = imageData.result.data.storagePath;
          imageName = imageData.result.data.name || imageName;
        } else if (imageData.result?.result?.data?.url) {
          imageUrl = imageData.result.result.data.url;
          storagePath = imageData.result.result.data.storagePath;
          imageName = imageData.result.result.data.name || imageName;
        } else if (imageData.result?.url) {
          imageUrl = imageData.result.url;
          storagePath = imageData.result.storagePath;
          imageName = imageData.result.name || imageName;
        } else if (imageData.data?.url) {
          imageUrl = imageData.data.url;
          storagePath = imageData.data.storagePath;
          imageName = imageData.data.name || imageName;
        } else if (imageData.url) {
          imageUrl = imageData.url;
          storagePath = imageData.storagePath;
          imageName = imageData.name || imageName;
        }

        if (imageUrl) {
          generatedImageAttachments = [
            createAttachment({ url: imageUrl, storagePath, name: imageName }, 0, metadata),
          ];
        }
      }
    } catch (error) {
      console.error(
        "❌ [Image Extract] Error converting image to attachment:",
        error
      );
    }
  }

  // Combine existing file attachments with generated image attachments
  const allFileAttachments = [
    ...(fileAttachmentsData.files || []),
    ...generatedImageAttachments,
  ];

  return {
    hasAnyToolData: true,
    hasToolCalls: toolCallsData.hasToolCalls,
    hasDeepResearch: deepResearchData.hasDeepResearch,
    hasFileAttachments:
      fileAttachmentsData.hasFileAttachments ||
      generatedImageAttachments.length > 0,

    // Tool calls data
    toolCalls: toolCallsData.toolCalls,

    // Specific tool data
    canvasData,
    imageData,
    webSearchData,

    // Deep research data
    deepResearchData: deepResearchData.data,
    isDeepResearchMessage: deepResearchData.hasDeepResearch,

    // File attachments (includes generated images)
    fileAttachments: allFileAttachments,
  };
}

/**
 * MAIN FUNCTION: Extract specific type of tool data
 * @param {Object} message - The message object
 * @param {string} type - Type to extract: 'toolCalls', 'canvas', 'image', 'webSearch', 'deepResearch', 'fileAttachments'
 * @param {boolean} isUser - Whether this is a user message (needed for deep research)
 * @returns {*} - Extracted data for the specific type
 */
export function extractSpecificToolData(message, type, isUser = false) {
  if (!message) return null;

  switch (type) {
    case "toolCalls":
      return extractToolCallsData(message);
    case "canvas":
      return extractCanvasData(message);
    case "image":
      return extractImageData(message);
    case "webSearch":
      return extractWebSearchData(message);
    case "deepResearch":
      return extractDeepResearchData(message, isUser);
    case "fileAttachments":
      return extractFileAttachmentsData(message);
    default:
      return null;
  }
}

// ============================================================================
// INTERNAL EXTRACTION FUNCTIONS (simplified from the original complex logic)
// ============================================================================

function extractToolCallsData(message) {
  const toolCalls = parseToolCalls(message);
  return {
    hasToolCalls: toolCalls.length > 0,
    toolCalls,
  };
}

function extractCanvasData(message) {
  const { hasToolCalls, toolCalls } = extractToolCallsData(message);
  if (!hasToolCalls) {
    return null;
  }

  // Find canvas tool call
  const canvasCall = toolCalls.find((call) => {
    const toolName = call.function?.name || call.name;
    const isCanvasTool =
      (toolName === "canvas_create" ||
        toolName === "canvas_stream" ||
        toolName === "canvas_edit") &&
      call.result;

    return isCanvasTool;
  });

  if (!canvasCall) {
    return null;
  }

  const result = canvasCall.result;

  // Check if the canvas result is an error
  // Preserve error information for display via AIErrorDisplay
  if (
    result.error === true ||
    result.isError === true ||
    result.success === false
  ) {
    return {
      error: true,
      isError: true,
      message: result.message || (typeof result.error === "string" ? result.error : "Canvas creation failed"),
      details: result.details,
      timestamp: result.timestamp,
    };
  }

  // Extract canvas data from different possible structures
  if (result.canvasData) {
    return result.canvasData;
  }

  if (result.content || result.blocks) {
    const extracted = {
      content: result.content || "",
      blocks: result.blocks || [],
      contentType: result.contentType || result.type || "text",
      type: result.contentType || result.type || "text",
      language: result.language || "javascript",
      name: result.name,
      metadata: result.metadata || {},
    };
    return extracted;
  }

  return result;
}

function extractImageData(message) {
  const { hasToolCalls, toolCalls } = extractToolCallsData(message);
  if (!hasToolCalls) return null;

  // Find image generation tool call
  const imageCall = toolCalls.find((call) => {
    const toolName = call.function?.name || call.name;
    return (
      (toolName === "image_gen" ||
        toolName === "image_generation" ||
        toolName === "image_edit") &&
      call.result
    );
  });

  if (!imageCall) return null;

  // Return the full result for ImageArtifact to extract from
  return imageCall.result;
}

function extractWebSearchData(message) {
  // First check for Perplexity citations in metadata (no tool calls needed)
  if (
    message.metadata?.citations &&
    Array.isArray(message.metadata.citations) &&
    message.metadata.citations.length > 0
  ) {
    // Convert Perplexity citations to web search data format
    // Perplexity returns citations as an array of URL strings, not objects
    const citations = message.metadata.citations;

    const validSources = citations
      .map((citation, index) => {
        // Handle URL strings (Perplexity format)
        if (typeof citation === "string" && citation.startsWith("http")) {
          try {
            const urlObj = new URL(citation);
            // Extract title from URL path - prefer longer, more descriptive segments
            let extractedTitle = null;
            const pathSegments = urlObj.pathname
              .split("/")
              .filter((seg) => seg && seg.length > 2)
              .map((seg) => decodeURIComponent(seg));

            if (pathSegments.length > 0) {
              // Prefer the longest segment (usually contains the article title)
              const sortedSegments = pathSegments.sort(
                (a, b) => b.length - a.length
              );
              const bestSegment = sortedSegments[0];

              // Format the segment as a title
              extractedTitle = bestSegment
                .replace(/[-_]/g, " ")
                .replace(/\.[^.]+$/, "") // Remove file extension
                .replace(/^\d+[-_]?/, "") // Remove leading numbers/IDs
                .trim();

              // Only use if it's meaningful (not just an ID or short code)
              if (extractedTitle && extractedTitle.length > 10) {
                // Capitalize first letter of each word
                extractedTitle = extractedTitle
                  .split(" ")
                  .map((word) => {
                    // Preserve acronyms and special cases
                    if (word.match(/^[A-Z]{2,}$/)) return word;
                    return (
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                    );
                  })
                  .join(" ");
              } else if (
                extractedTitle &&
                extractedTitle.length > 3 &&
                extractedTitle.length <= 10
              ) {
                // Short but potentially meaningful (like article IDs)
                extractedTitle = extractedTitle.toUpperCase();
              } else {
                extractedTitle = null;
              }
            }

            return {
              url: citation,
              title: extractedTitle || null, // Don't use hostname as title - let MLA formatter handle it
              description: "",
              favicon: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`,
            };
          } catch (e) {
            console.warn(
              "[extractWebSearchData] Failed to parse citation URL:",
              citation,
              e
            );
            return null;
          }
        }
        // Handle object format (if any) - this is the preferred format with titles from search_results
        if (citation && citation.url) {
          try {
            const urlObj = new URL(citation.url);
            // Use title from citation object if available, otherwise extract from URL
            let title = citation.title || citation.name;
            if (!title) {
              // Extract title from URL path as fallback
              const pathSegments = urlObj.pathname
                .split("/")
                .filter((seg) => seg && seg.length > 2)
                .map((seg) => decodeURIComponent(seg));

              if (pathSegments.length > 0) {
                // Prefer the longest segment (usually contains the article title)
                const sortedSegments = pathSegments.sort(
                  (a, b) => b.length - a.length
                );
                const bestSegment = sortedSegments[0];

                // Format the segment as a title
                const extractedTitle = bestSegment
                  .replace(/[-_]/g, " ")
                  .replace(/\.[^.]+$/, "") // Remove file extension
                  .replace(/^\d+[-_]?/, "") // Remove leading numbers/IDs
                  .trim();

                // Only use if it's meaningful
                if (extractedTitle && extractedTitle.length > 10) {
                  title = extractedTitle
                    .split(" ")
                    .map((word) => {
                      if (word.match(/^[A-Z]{2,}$/)) return word;
                      return (
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                      );
                    })
                    .join(" ");
                } else if (
                  extractedTitle &&
                  extractedTitle.length > 3 &&
                  extractedTitle.length <= 10
                ) {
                  title = extractedTitle.toUpperCase();
                }
              }
            }

            return {
              url: citation.url,
              title: title || null, // Don't use hostname as title
              description: citation.description || citation.snippet || "",
              favicon:
                citation.favicon ||
                `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`,
            };
          } catch (e) {
            console.warn(
              "[extractWebSearchData] Failed to parse citation object:",
              citation,
              e
            );
            return null;
          }
        }
        return null;
      })
      .filter(Boolean);

    if (validSources.length > 0) {
      return {
        data: validSources,
        sources: validSources,
        citations: citations, // Keep original citations for reference
      };
    } else {
      // Citations were detected but failed to convert - return empty result to prevent fallthrough
      // Return empty structure to indicate citations were present but couldn't be processed
      // This prevents falling through to tool calls extraction logic
      return {
        data: [],
        sources: [],
        citations: citations, // Keep original citations for reference
      };
    }
  }

  const { hasToolCalls, toolCalls } = extractToolCallsData(message);
  if (!hasToolCalls) return null;

  // Find web search tool call
  const webSearchCall = toolCalls.find((call) => {
    const toolName = call.function?.name || call.name;
    return (
      (toolName === "web_search" || toolName === "firecrawl_search") &&
      call.result
    );
  });

  if (!webSearchCall) return null;

  const result = webSearchCall.result;

  // Check if the web search result is an error
  // Preserve error information for display via AIErrorDisplay
  if (
    result.error === true ||
    result.isError === true ||
    result.success === false
  ) {
    return {
      error: true,
      isError: true,
      message: result.message || (typeof result.error === "string" ? result.error : "Web search failed"),
      details: result.details,
      timestamp: result.timestamp,
    };
  }

  // OPTION 1 FIX: Format sources for Markdown and SourcesAvatars components
  // Both components expect sources.data to contain the sources array
  // Web search tool returns { sources: [array], data: [full results array] }
  // We need to ensure sources.data contains the sources array (citations only)

  // Check if result has sources array (the citations)
  if (
    result.sources &&
    Array.isArray(result.sources) &&
    result.sources.length > 0
  ) {
    // Filter sources to only include those with URLs (valid citations)
    const validSources = result.sources.filter((source) => {
      return source && source.url && source.url !== null && source.url !== "";
    });

    if (validSources.length > 0) {
      // Format so that sources.data contains the sources array
      // This allows Markdown and SourcesAvatars components to find it via sources.data
      const formattedResult = {
        ...result,
        data: validSources, // Put valid sources array in data property
        sources: validSources, // Keep sources for backward compatibility
      };

      return formattedResult;
    }
  }

  // If no sources array, try to extract from data array
  // Web search data array contains all results - we want to skip the first one (main result)
  // and use the rest as sources
  if (result.data && Array.isArray(result.data) && result.data.length > 1) {
    // Skip first item (main result) and use rest as sources
    const sourcesFromData = result.data.slice(1).filter((item) => {
      return item && item.url && item.url !== null && item.url !== "";
    });

    if (sourcesFromData.length > 0) {
      const formattedResult = {
        ...result,
        data: sourcesFromData, // Put sources array in data property
        sources: sourcesFromData, // Keep sources for backward compatibility
      };

      return formattedResult;
    }
  }

  // Handle different data structures
  if (result.data && Array.isArray(result.data)) {
    return result;
  }

  if (Array.isArray(result)) {
    return { data: result };
  }

  if (result.results && Array.isArray(result.results)) {
    return { data: result.results };
  }

  return result;
}

function extractDeepResearchData(message, isUser) {
  // Check if this is a deep research message
  const hasDeepResearch =
    message.metadata?.isDeepResearch ||
    message.metadata?.hasDeepResearch ||
    message.metadata?.isDeepResearchResult ||
    message.metadata?.isDeepResearchInProgress ||
    message.metadata?.deepResearch ||
    message.metadata?.originalQueryMessageId;

  if (!hasDeepResearch) {
    return { hasDeepResearch: false, data: null };
  }

  // Get deep research data directly or reconstruct from metadata
  let data = message.metadata?.deepResearch;

  if (!data) {
    // Reconstruct from legacy metadata structure
    const isComplete = message.metadata?.isDeepResearchResult === true;
    const isInProgress = message.metadata?.isDeepResearchInProgress === true;

    data = {
      status: isComplete ? "complete" : isInProgress ? "active" : "complete",
      query:
        message.metadata?.researchQuery || message.content || "Research query",
      activity: message.metadata?.activities || [],
      sources: message.metadata?.sources || [],
      finalAnalysis: message.content || "",
      activitiesCount: message.metadata?.activitiesCount || 0,
      sourcesCount: message.metadata?.sourcesCount || 0,
      elapsedTime:
        message.metadata?.researchElapsedTime ||
        message.metadata?.elapsedTime ||
        0,
      startTime: message.metadata?.startTime,
      endTime: message.metadata?.endTime,
      estimatedActivities: message.metadata?.estimatedActivities,
      remainingActivities: message.metadata?.remainingActivities,
      progressPercentage:
        message.metadata?.progressPercentage || (isComplete ? 100 : 0),
    };
  }

  return {
    hasDeepResearch: true,
    data,
  };
}

function extractFileAttachmentsData(message) {
  // Check attachments array
  if (message.attachments?.length > 0) {
    return {
      hasFileAttachments: true,
      files: message.attachments,
    };
  }

  // Check metadata storage_files using shared utility
  const files = parseStorageFiles(message.metadata);
  if (files.length > 0) {
    return {
      hasFileAttachments: true,
      files,
    };
  }

  return {
    hasFileAttachments: false,
    files: [],
  };
}

// ============================================================================
// LEGACY COMPATIBILITY FUNCTIONS (to be gradually phased out)
// ============================================================================

// Keep these for backward compatibility during the transition
export function useToolData(message, metadata, toolOutput, isUser) {
  const toolData = extractToolData(message, isUser);

  return {
    hasToolCalls: toolData.hasToolCalls,
    toolCallOutput: toolOutput,
    toolCalls: toolData.toolCalls,
    dalleImageData: toolData.imageData,
    chartData: null, // Removed
    webSearchData: toolData.webSearchData,
    canvasData: toolData.canvasData,
    isDeepResearchMessage: toolData.isDeepResearchMessage,
    deepResearchData: toolData.deepResearchData,
  };
}

export function useHasToolCalls(message) {
  return hasToolData(message);
}
